import type { JobDetailDto, JobEdits, JobStatus, JobSummaryDto, MeResponse, MilestoneDto, SaveJobRequest } from "@workcost/shared";
import { apiFetch, apiKeepalive } from "./api";
import { EstimateApiError } from "./estimateApi";
import { uploadPhotos } from "./upload";

/** เฉพาะ dev และตั้ง VITE_MOCK_API=1 เท่านั้น (production ตัดทิ้ง) */
async function loadMockJobs(): Promise<typeof import("../mock/jobs") | null> {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") return import("../mock/jobs");
  return null;
}

async function call<T>(path: string, init: { method: string; json?: unknown } = { method: "GET" }): Promise<T> {
  const res = await apiFetch(path, init);
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; reason?: ConstructorParameters<typeof EstimateApiError>[2] };
    throw new EstimateApiError(res.status, b.error ?? "error", b.reason);
  }
  return body as T;
}

const enc = encodeURIComponent;

/** ข้อมูลผู้ใช้จำลอง (null เมื่อไม่ได้เปิดโหมด mock) */
export async function mockMe(): Promise<MeResponse | null> {
  const mock = await loadMockJobs();
  return mock ? mock.me() : null;
}

/** GET /jobs */
export async function listJobs(): Promise<JobSummaryDto[]> {
  const mock = await loadMockJobs();
  if (mock) return mock.listJobs();
  return (await call<{ items: JobSummaryDto[] }>("/jobs")).items;
}

/** GET /jobs/{id} */
export async function getJob(id: string): Promise<JobDetailDto> {
  const mock = await loadMockJobs();
  if (mock) return mock.getJob(id);
  return call<JobDetailDto>(`/jobs/${enc(id)}`);
}

/** POST /jobs (402 save_limit เมื่อ Free บันทึกครบ) */
export async function saveJob(req: SaveJobRequest): Promise<JobDetailDto> {
  const mock = await loadMockJobs();
  if (mock) return mock.saveJob(req);
  return call<JobDetailDto>("/jobs", { method: "POST", json: req });
}

export interface JobPatch {
  title?: string;
  customer?: { name: string; phone?: string; address?: string };
  plannedStart?: string | null;
  plannedFinish?: string | null;
  actualStart?: string | null;
  actualFinish?: string | null;
  progressOverride?: number | null;
}

/** PATCH /jobs/{id} */
export async function patchJob(id: string, patch: JobPatch): Promise<JobDetailDto> {
  const mock = await loadMockJobs();
  if (mock) return mock.patchJob(id, patch);
  return call<JobDetailDto>(`/jobs/${enc(id)}`, { method: "PATCH", json: patch });
}

/** DELETE /jobs/{id} */
export async function deleteJob(id: string): Promise<void> {
  const mock = await loadMockJobs();
  if (mock) return mock.deleteJob(id);
  await call<{ ok: boolean }>(`/jobs/${enc(id)}`, { method: "DELETE" });
}

/** DELETE /jobs/{id} แบบ keepalive สำหรับตอนปิดหน้า (pagehide) คืน true เมื่อส่งออกไปแล้ว ไม่รอผล โหมด mock ไม่ส่ง */
export function deleteJobKeepalive(id: string): boolean {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") return false;
  return apiKeepalive(`/jobs/${enc(id)}`, "DELETE");
}

/** PUT /jobs/{id}/estimate */
export async function putEstimate(id: string, edits: JobEdits): Promise<JobDetailDto> {
  const mock = await loadMockJobs();
  if (mock) return mock.putEstimate(id, edits);
  return call<JobDetailDto>(`/jobs/${enc(id)}/estimate`, { method: "PUT", json: { edits } });
}

/** POST /jobs/{id}/status (กติกาตรวจซ้ำฝั่ง server เสมอ) */
export async function changeStatus(id: string, body: { to: JobStatus; note?: string; photoKey?: string }): Promise<JobDetailDto> {
  const mock = await loadMockJobs();
  if (mock) return mock.changeStatus(id, body);
  return call<JobDetailDto>(`/jobs/${enc(id)}/status`, { method: "POST", json: body });
}

/** POST /jobs/{id}/milestones */
export async function addMilestone(id: string, body: { label: string; amount_thb: number; dueDate?: string }): Promise<{ milestoneId: string }> {
  const mock = await loadMockJobs();
  if (mock) return mock.addMilestone(id, body);
  return call<{ milestoneId: string }>(`/jobs/${enc(id)}/milestones`, { method: "POST", json: body });
}

/** PATCH /jobs/{id}/milestones/{mid} */
export async function patchMilestone(id: string, mid: string, body: Partial<Pick<MilestoneDto, "label" | "amount_thb">> & { dueDate?: string | null; paidAt?: string | null }): Promise<void> {
  const mock = await loadMockJobs();
  if (mock) return mock.patchMilestone(id, mid, body);
  await call<{ ok: boolean }>(`/jobs/${enc(id)}/milestones/${enc(mid)}`, { method: "PATCH", json: body });
}

/** อัปโหลดรูปแนบการเปลี่ยนสถานะ (presign, PUT, verify) แล้วคืน key */
export async function uploadStatusPhoto(photo: Blob): Promise<string> {
  const mock = await loadMockJobs();
  if (mock) return mock.uploadStatusPhoto();
  const [key] = await uploadPhotos([photo]);
  if (!key) throw new Error("upload_failed");
  return key;
}
